import React from "react";
import axios from "axios";

import Header from "./../../assets/components/header";
import AccountSummary from "./../AccountSummary";
import FeedbackButton from "./../AccountSummary/FeedbackButton";
import StrategyPortalButton from "./../AccountSummary/StrategyPortalButton";
import Constants from "./../../Utils/Constants";
import store from "./../../Common/storage";
import { getHeaders } from "./../../Utils/Common";

const NAVY = "#0D2645";
const CYAN = "#00AAF6";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

// ── Stat card with icon bubble ─────────────────────────────────────────────
function StatCard({ label, value, icon, iconBg, valueColor }) {
  return (
    <div style={{
      flex: "1 1 140px",
      background: "#fff",
      borderRadius: "10px",
      padding: "16px 18px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      border: "1px solid #F0F0F5",
    }}>
      <div style={{
        width: "44px", height: "44px", flexShrink: 0,
        borderRadius: "10px",
        background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "20px",
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: valueColor || NAVY, lineHeight: 1 }}>
          {value ?? <span style={{ fontSize: "16px", color: "#BABABA" }}>—</span>}
        </div>
        <div style={{ fontSize: "11px", fontWeight: 600, color: "#6B6C7E", marginTop: "3px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {label}
        </div>
      </div>
    </div>
  );
}

// ── Action card with navy top strip ───────────────────────────────────────
function ActionCard({ icon, title, description, onClick, restricted, accent }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={restricted ? undefined : onClick}
      onMouseEnter={() => !restricted && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: "1 1 180px",
        background: "#fff",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: hovered
          ? "0 10px 28px rgba(13,38,69,0.14)"
          : "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #F0F0F5",
        cursor: restricted ? "default" : "pointer",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        opacity: restricted ? 0.45 : 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Colored top strip */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #16375e 100%)`,
        padding: "22px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Decorative circle */}
        <div style={{
          position: "absolute", right: -20, top: -20,
          width: 90, height: 90, borderRadius: "50%",
          background: `rgba(0,170,246,0.12)`,
        }} />
        <span style={{ fontSize: "34px", lineHeight: 1, position: "relative" }}>{icon}</span>
        {!restricted && (
          <div style={{
            width: "28px", height: "28px",
            borderRadius: "50%",
            background: hovered ? CYAN : "rgba(255,255,255,0.1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.2s ease",
            position: "relative",
          }}>
            <span style={{ fontSize: "13px", color: "#fff", fontWeight: 700, lineHeight: 1 }}>→</span>
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: "14px 16px 18px", flexGrow: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
        <div style={{
          fontSize: "12px", fontWeight: 800, color: NAVY,
          textTransform: "uppercase", letterSpacing: "0.05em",
        }}>
          {title}
        </div>
        <div style={{ fontSize: "12px", color: "#6B6C7E", lineHeight: 1.6, flexGrow: 1 }}>
          {description}
        </div>
        {!restricted && (
          <div style={{
            marginTop: "10px",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            background: hovered ? CYAN : NAVY,
            color: "#fff",
            borderRadius: "5px",
            padding: "6px 12px",
            fontSize: "10px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            transition: "background 0.2s ease",
            alignSelf: "flex-start",
          }}>
            Open
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function AccountSummaryV2(props) {
  const user    = store.get("user");
  const partner = user?.partner;
  const firstName = user?.first_name || (user?.name ? user.name.split(" ")[0] : "there");

  const [autoStats, setAutoStats] = React.useState(null);
  const [credits,   setCredits]   = React.useState(null);

  React.useEffect(() => {
    axios
      .get(
        Constants.getApiPathWithConfigId(Constants.historyApiPath) +
          "?per_page=1&page=1&order_by=id&order=desc",
        getHeaders()
      )
      .then(r => setAutoStats(r.data.meta))
      .catch(() => {});

    axios
      .get(Constants.getCreditsApiPath, getHeaders())
      .then(r => setCredits(r.data?.data?.credits ?? r.data?.credits ?? null))
      .catch(() => {});
  }, []);

  // Delegate non-dashboard screens to the original component
  const needsSetup =
    store.get("user").configs[store.get("c_id_index")]?.host === "" ||
    store.get("user").configs[store.get("c_id_index")]?.api_key === "";
  const needsSelection =
    store.get("user").configs.length > 1 && !store.get("c_id");

  if (needsSetup || needsSelection) return <AccountSummary {...props} />;

  // Derived stats
  const total      = autoStats?.total         ?? null;
  const failed     = autoStats?.failed_count  ?? null;
  const delivered  = total !== null ? total - failed : null;
  const delivRate  = total > 0 ? `${((delivered / total) * 100).toFixed(1)}%` : null;
  const linkClicks = autoStats?.link_clicked  ?? null;

  const isRestricted = Constants.restrictAccounts.includes(user.id);

  const cards = [
    {
      icon: "📝",
      title: "SMS Templates",
      description: `Build and organise your message library for all ${partner?.whitelabel_name} campaigns and sequences.`,
      onClick: () => Constants.navigate(Constants.smstable),
      restricted: isRestricted,
    },
    {
      icon: "📤",
      title: "SMS Campaigns",
      description: `Send an SMS or MMS campaign to your ${partner?.whitelabel_name} lists and segments in minutes.`,
      onClick: () => Constants.navigate(Constants.smsautomation),
      restricted: isRestricted,
    },
    {
      icon: "📞",
      title: "Training Call",
      description: "Book a complimentary 1:1 session with a VYBRNT specialist for SMS and CRM optimisation.",
      onClick: () => window.open("tel:+61450611664"),
      restricted: false,
    },
    {
      icon: "📧",
      title: `Share ${partner?.whitelabel_name}`,
      description: `Refer a friend and give them a free trial of ${partner?.whitelabel_name}.`,
      onClick: () =>
        window.open(
          `mailto:?subject=Here's a Free Trial to ${partner?.whitelabel_name}.` +
          `&body=Hey,%0D%0A%0D%0AHere's a link to a free trial of ${partner?.whitelabel_name}: ` +
          `${partner?.whitelabel_link}%0D%0A%0D%0ABest Regards,`
        ),
      restricted: false,
    },
  ];

  return (
    <div className="panal-viewport middle-container">
      <Header name={"Dashboard"} />

      {/* ── Hero banner ── */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #132f5a 100%)`,
        borderRadius: "12px",
        padding: "24px 28px",
        marginBottom: "20px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Decorative circles */}
        <div style={{ position: "absolute", right: -50, top: -50, width: 220, height: 220, borderRadius: "50%", background: "rgba(0,170,246,0.07)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", right: 80, bottom: -80, width: 180, height: 180, borderRadius: "50%", background: "rgba(0,170,246,0.05)", pointerEvents: "none" }} />

        <div style={{ position: "relative" }}>
          <h2 style={{ margin: 0, color: "#fff", fontSize: "20px", fontWeight: 700, letterSpacing: "-0.2px" }}>
            Good {greeting()}, {firstName} 👋
          </h2>
          <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.55)", fontSize: "13px" }}>
            Here's what's happening with your SMS today.
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "16px", flexWrap: "wrap" }}>
            {credits !== null && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "7px",
                background: "rgba(0,170,246,0.15)",
                border: "1px solid rgba(0,170,246,0.3)",
                borderRadius: "20px",
                padding: "5px 14px",
              }}>
                <span style={{ fontSize: "13px" }}>💳</span>
                <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>SMS Credits</span>
                <span style={{ fontSize: "14px", fontWeight: 800, color: CYAN }}>{Number(credits).toLocaleString()}</span>
              </div>
            )}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "7px",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "20px",
              padding: "5px 14px",
            }}>
              <span style={{ fontSize: "13px" }}>🏢</span>
              <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                {partner?.whitelabel_name}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stat strip ── */}
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
        <StatCard
          label="Total Sent"
          value={total !== null ? Number(total).toLocaleString() : null}
          icon="📤"
          iconBg="#EEF2FF"
          valueColor={NAVY}
        />
        <StatCard
          label="Delivered"
          value={delivered !== null ? Number(delivered).toLocaleString() : null}
          icon="✅"
          iconBg="#E8F9EE"
          valueColor="#28a745"
        />
        <StatCard
          label="Delivery Rate"
          value={delivRate}
          icon="📈"
          iconBg="#E8F9EE"
          valueColor="#28a745"
        />
        <StatCard
          label="Link Clicks"
          value={linkClicks !== null ? Number(linkClicks).toLocaleString() : null}
          icon="🔗"
          iconBg="#EAF5FF"
          valueColor={CYAN}
        />
      </div>

      {/* ── Action cards ── */}
      <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "24px" }}>
        {cards.map(c => <ActionCard key={c.title} {...c} />)}
      </div>

      <FeedbackButton />
      <StrategyPortalButton />
    </div>
  );
}
